import type { Customer } from '@/model/schemas';

import type { ApiResponse } from '../http/BaseHttpClient';
import { AbstractService } from './AbstractService';

export type CustomerType = 'REGULAR' | 'WHOLESALER' | 'RESELLER';

// Internal payload type used by AuthService.
type AuthPayload = Record<string, unknown>;
export type AuthResponse = Record<string, unknown>;

export interface IRegisterData {
  name: string;
  email: string;
  password: string;
  confirmPassword?: string;
  customer_type: CustomerType;
  enterprise?: string;
  tax_id?: string;
}

export interface ISession {
  customer: Customer | null;
  /**
   * Legacy alias for older callers.
   */
  user?: Customer | null;
  token: string | null;
  isAuthenticated: boolean;
}

/**
 * Auth service - handles customer login/registration and session lookup.
 */
export class AuthService extends AbstractService {
  async getCurrentUser(token?: string): Promise<AuthResponse> {
    const response = await this.get<AuthResponse>(this.endpoint('authMe'), undefined, { token });
    return (response.data as AuthResponse) ?? (response as unknown as AuthResponse);
  }

  async getSession(token?: string): Promise<ISession> {
    try {
      const response = await this.get<ApiResponse<{ data?: Customer }>>(this.endpoint('authMe'), undefined, { token });
      const customer = (response.data?.data ?? response.data) as Customer | undefined;
      if (customer) {
        return { customer, user: customer, token: token || null, isAuthenticated: true };
      }
      return { customer: null, user: null, token: null, isAuthenticated: false };
    } catch (_) {
      return { customer: null, token: null, isAuthenticated: false };
    }
  }

  async register(data: IRegisterData): Promise<AuthResponse> {
    return this.customerRegister(data);
  }

  async customerRegister(data: IRegisterData): Promise<AuthResponse> {
    const payload: AuthPayload = {
      name: data.name,
      email: data.email,
      password: data.password,
      customer_type: data.customer_type,
      enterprise: data.enterprise,
      tax_id: data.tax_id,
    };

    try {
      const response = await this.post<AuthResponse>(this.endpoint('customers'), payload);
      return response;
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const response: any = (err as any).response;
        const status = response?.status;
        const msg = response?.data?.message || response?.data?.error?.message || (err as any).message;
        const e = new Error(msg);
        (e as any).status = status;
        throw e;
      }
      throw err;
    }
  }

  async businessRegister(data: IRegisterData): Promise<AuthResponse> {
    const customerType = data.customer_type ?? 'WHOLESALER';
    return this.customerRegister({ ...data, customer_type: customerType });
  }

  async login(email: string, password: string, customer_type?: string): Promise<AuthResponse> {
    return this.loginCustomer(email, password, customer_type);
  }

  async loginCustomer(email: string, password: string, customer_type?: string): Promise<AuthResponse> {
    const payload: AuthPayload = { email, password };
    if (customer_type) payload.customer_type = customer_type;

    try {
      const response = await this.post<AuthResponse>(this.endpoint('authLogin'), payload);
      return response;
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const response: any = (err as any).response;
        const status = response?.status;
        const msg = response?.data?.message || response?.data?.error?.message || (err as any).message;
        const e = new Error(msg);
        (e as any).status = status;
        throw e;
      }
      throw err;
    }
  }

  async logout(token?: string): Promise<void> {
    try {
      await this.post(this.endpoint('authLogout'), undefined, { token });
    } catch (err) {
      // Fail silently; logout is primarily client-side state clearing.
      this.logger.warn({ msg: 'Logout request failed', error: err });
    }
  }

  async requestPasswordReset(email: string): Promise<AuthResponse> {
    const payload: AuthPayload = { email };
    const response = await this.post<AuthResponse>(this.endpoint('customerForgotPassword'), payload);
    return (response.data as AuthResponse) ?? (response as unknown as AuthResponse);
  }

  async resetPassword(token: string, password: string): Promise<AuthResponse> {
    const payload: AuthPayload = { token, password };
    const response = await this.post<AuthResponse>(this.endpoint('customerResetPassword'), payload);
    return (response.data as AuthResponse) ?? (response as unknown as AuthResponse);
  }

  /**
   * Aliases used by theme components.
   */
  async forgotPasswordCustomer(email: string): Promise<AuthResponse> {
    return this.requestPasswordReset(email);
  }

  async resetPasswordCustomer(token: string, password: string): Promise<AuthResponse> {
    return this.resetPassword(token, password);
  }
}

export const authService = new AuthService();

export default AuthService;
